import { useEffect, useState, useRef } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Box } from "@mui/material";

//Import Components
import Header from "../Header/Header";
import Sidebar from "../Sidebar/Sidebar";
import InfoBanner from "../Banner/InfoBanner";
import Footer from "../Footer/Footer";

//Import Pages
import ViewProjects from "../../pages/Projects/ViewProjects";
import Dashboard from "../../pages/Dashboard/Dashboard";
import EditProject from "../../pages/Projects/EditProject";
import ReviewProject from "../../pages/Projects/ReviewProject";
import StrategyTypes from "../../pages/Projects/StrategyTypes";
import AddNewProject from "../../pages/Projects/AddNewProject/index";
import Regions from "../../pages/Regions/Regions";
import Users from "../../pages/Users/Users";
import ViewRoadways from "../../pages/Roadways/ViewRoadways";
import EditRoadway from "../../pages/Roadways/EditRoadway";
import ConnectedProjects from "../../pages/Roadways/ConnectedProjects";
import LoginUser from "../../pages/Login/LoginUser";
import ViewSelectedRoadway from "../../pages/Roadways/ViewSelectedRoadway";
import HomePage from "../../pages/Home/HomePage";
import Top100RoadwaysPage from "../../pages/Home/Top100RoadwaysPage";
import ResetPassword from "../../pages/Login/ResetPassword";
import NonSystemUsers from "../../pages/Users/NonSystemUsers";

//Import context
import { useAuth, RequireAuth } from "../../context/AuthContext";
import { ConnectedProjectsDataProvider } from "../../context/ConnectedProjectsDataContext";
import { useRoadwaysBannerMsg } from "../../context/RoadwaysBannerContext";

//constants
import { NO_SIDEBAR_LOGOUT_PAGES, HEADER_CONSTANTS, PAGE_TITLES } from "../../Constants";

export default function BasicLayout() {
  const showInfoBanner = process.env.REACT_APP_SHOW_INFO_BANNER === 'true';
  const roadwaysBannerMsg = useRoadwaysBannerMsg();
  const [isInfoBannerVisible, setIsInfoBannerVisible] = useState(showInfoBanner || roadwaysBannerMsg?.text);
  const { auth } = useAuth();
  const currentUserPrivileges = auth?.user?.privileges
    ? auth?.user?.privileges
    : null;
  const userRole = auth?.user?.roleName?.toLowerCase() || "";
  const location = useLocation();
  const isNoSidebarLogoutPages = NO_SIDEBAR_LOGOUT_PAGES.includes(
    location.pathname
  );
  const headerTitle = HEADER_CONSTANTS.WEBSITE_HEADER.includes(
    location.pathname
  )
    ? HEADER_CONSTANTS.MITIGATION_PLANS
    : HEADER_CONSTANTS.MITIGATION_PLAN_SYSTEM;
  const pagesWithSubHeader = ["/", "/top100Roadways"];
  const hasSubHeader = pagesWithSubHeader.includes(location.pathname);
  const isFirstMount = useRef(true);
  let contentPaddingLeft = 0;
  if (!isNoSidebarLogoutPages) {
    contentPaddingLeft = 30;
    if (userRole === "super admin")
      contentPaddingLeft = 40;
  }

  //callback to handle banner visibility changes
  const handleInfoBannerVisibilityChange = (isVisible) => {
    setIsInfoBannerVisible(isVisible);
  };

  //calculate padding based on header configuration
  const getContentPaddingTop = () => {
    if (location.pathname === "/login" || location.pathname === "/reset-password") {
      return 0;
    }
    let padding = hasSubHeader ? 12 : 10;
    if (isInfoBannerVisible) {
      padding += hasSubHeader ? 4 : 8;
    }
    return padding;
  };

  // Set the document title based on the header title
  useEffect(() => {
    const pageTitle = PAGE_TITLES[location.pathname] || "";
    document.title = pageTitle !== "" ? `${pageTitle} | ${headerTitle}` : headerTitle;

    //reset focus to top of page on route change for better accessibility
    window.scrollTo(0, 0);

    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    const mainContent = document.getElementById("main-content");
    if (mainContent) {
      mainContent.focus();
    }
  }, [location.pathname]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>

      {/* For 508 compliance add skip to main content link visible to screen readers */}
      <Box
        component="a"
        href="#main-content"
        onClick={() => {
          const target = document.getElementById("main-content");
          if (target) {
            target.focus();
          }
        }}
        tabIndex={0}
        sx={{
          position: 'fixed',
          top: -100,
          left: 0,
          width: '100%',
          zIndex: 9999,
          backgroundColor: 'primary.main',
          color: 'white',
          textAlign: 'center',
          padding: 2,
          textDecoration: 'none',
          fontWeight: 'bold',
          transition: 'top 0.3s ease',
          '&:focus': { top: 0 }
        }}
      >
        Skip to main content
      </Box>
      <Header showLogout={!isNoSidebarLogoutPages} title={headerTitle} />
      <InfoBanner
        hasSubHeader={hasSubHeader}
        onVisibilityChange={handleInfoBannerVisibilityChange}
        roadwaysBannerMsg={roadwaysBannerMsg}
      />
      <Box sx={{ display: "flex", flex: 1 }}>
        {!isNoSidebarLogoutPages && <Sidebar isBannerVisible={isInfoBannerVisible} hasBothBanners={showInfoBanner && !!roadwaysBannerMsg?.text} />}
        <Box
          component="main"
          tabIndex={-1} id="main-content"
          sx={{
            paddingTop: getContentPaddingTop(),
            paddingLeft: contentPaddingLeft,
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            outline: 'none'
          }}
        >
          {/* Add Routing to different components based on path. */}
          <Routes>
            {/* Website routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/top100Roadways" element={<Top100RoadwaysPage />} />
            <Route path='/reset-password' element={<ResetPassword />} />
            <Route path="/login" element={<LoginUser />} />

            {/* Portal routes with RequireAuth */}
            <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/view-projects" element={<RequireAuth><ViewProjects /></RequireAuth>} />
            <Route path="/edit-project" element={<RequireAuth><EditProject /></RequireAuth>} />
            <Route path="/review-project" element={<RequireAuth><ReviewProject /></RequireAuth>} />
            <Route path="/add-new-project" element={<RequireAuth><AddNewProject /></RequireAuth>} />
            <Route path="/view-roadways" element={<RequireAuth><ViewRoadways /></RequireAuth>} />
            <Route path="/view-selected-roadway" element={<RequireAuth><ViewSelectedRoadway /></RequireAuth>} />

            {/* For roadways */}
            <Route
              path="/edit-roadway"
              element={
                <RequireAuth>
                  {currentUserPrivileges?.roadways?.includes("update") ? (
                    <ConnectedProjectsDataProvider>
                      <EditRoadway />
                    </ConnectedProjectsDataProvider>
                  ) : <Navigate to="/dashboard" replace />}
                </RequireAuth>
              }
            />
            <Route
              path="/connected-projects"
              element={
                <RequireAuth>
                  {currentUserPrivileges?.roadways?.includes("update") ? (
                    <ConnectedProjectsDataProvider>
                      <ConnectedProjects />
                    </ConnectedProjectsDataProvider>
                  ) : <Navigate to="/dashboard" replace />}
                </RequireAuth>
              }
            />

            {/* For Strategy Types */}
            <Route
              path="/projects/strategy-types"
              element={
                <RequireAuth>
                  {userRole === "super admin" ? <StrategyTypes /> : <Navigate to="/dashboard" replace />}
                </RequireAuth>
              }
            />

            {/* For regions */}
            <Route
              path="/regions"
              element={
                <RequireAuth>
                  {userRole === "super admin" ? <Regions /> : <Navigate to="/dashboard" replace />}
                </RequireAuth>
              }
            />

            {/* For users */}
            <Route
              path="/users"
              element={
                <RequireAuth>
                  {currentUserPrivileges?.users?.includes("view") ? <Users /> : <Navigate to="/dashboard" replace />}
                </RequireAuth>
              }
            />

            {/* For non-system users management - only accessible to Super Admins */}
            <Route
              path="/non-system-users"
              element={
                <RequireAuth>
                  {userRole === "super admin" ? <NonSystemUsers /> : <Navigate to="/dashboard" replace />}
                </RequireAuth>
              }
            />

            {/* Redirect any unmatched path to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Box>
      </Box>

      {/* Accessible footer — appears on all pages */}
      <Footer disableTopMargin={isNoSidebarLogoutPages} />
    </Box>
  );
}